import { Component, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SesionService } from './core/sesion.service';
import { CrmService, EmbudoDto, ContactoDto } from './core/crm.service';

export interface ModuloMenuNexa {
  id: string;
  etiqueta: string;
  color: string;
  categoria: 'clinica' | 'atencion' | 'omnicanal';
  ruta: string;
  vista?: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  readonly sesion = inject(SesionService);
  readonly crm = inject(CrmService);
  readonly router = inject(Router);

  readonly embudos = signal<EmbudoDto[]>([]);
  readonly contactos = signal<ContactoDto[]>([]);
  readonly errorModal = signal<string | null>(null);
  readonly avisoClinico = signal<string | null>(null);
  readonly consultandoClinica = signal(false);

  // Estado interactivo del menú lateral Creatio Studio
  readonly filtroModulo = signal('');
  readonly categoriaModulo = signal<'todos' | 'clinica' | 'atencion' | 'omnicanal'>('todos');
  readonly menuCategoriasAbierto = signal(false);
  readonly urlActual = signal('/pendientes?vista=inicio');

  readonly modulosMenu: ModuloMenuNexa[] = [
    { id: 'inicio',      etiqueta: 'Inicio',              color: 'amber',   categoria: 'clinica',   ruta: '/pendientes',     vista: 'inicio' },
    { id: 'casos',       etiqueta: 'Casos Clínicos',      color: 'pink',    categoria: 'clinica',   ruta: '/pendientes',     vista: 'casos' },
    { id: 'escritorio',  etiqueta: 'Escritorio Médico',   color: 'purple',  categoria: 'clinica',   ruta: '/procesos',       vista: 'escritorio' },
    { id: 'quirurgicos', etiqueta: 'Procedimientos',      color: 'lime',    categoria: 'clinica',   ruta: '/procesos',       vista: 'quirurgicos' },
    { id: 'pacientes',   etiqueta: 'Expedientes 360°',    color: 'orange',  categoria: 'atencion',  ruta: '/pacientes' },
    { id: 'colas',       etiqueta: 'Control de Tiempos',  color: 'blue',    categoria: 'omnicanal', ruta: '/pendientes',     vista: 'colas' },
    { id: 'chats',       etiqueta: 'Chats y Redes',       color: 'blue',    categoria: 'omnicanal', ruta: '/conversaciones' },
    { id: 'convenios',   etiqueta: 'Convenios y Pólizas', color: 'red',     categoria: 'atencion',  ruta: '/pendientes',     vista: 'convenios' },
    { id: 'biometria',   etiqueta: 'Biometría & Citas',   color: 'teal',    categoria: 'clinica',   ruta: '/pendientes',     vista: 'biometria' },
    { id: 'actividad',   etiqueta: 'Actividad',           color: 'emerald', categoria: 'omnicanal', ruta: '/pendientes',     vista: 'actividad' },
  ];

  readonly pestanasAbiertas = signal<ModuloMenuNexa[]>([
    this.modulosMenu[0],
    this.modulosMenu[1],
  ]);

  readonly modulosFiltrados = computed(() => {
    const q = this.filtroModulo().trim().toLowerCase();
    const cat = this.categoriaModulo();
    return this.modulosMenu.filter(m => {
      if (cat !== 'todos' && m.categoria !== cat) return false;
      if (q && !m.etiqueta.toLowerCase().includes(q)) return false;
      return true;
    });
  });

  busquedaGlobal = '';

  // Formulario Nuevo Caso
  casoEmbudoId = '';
  casoEtapaId = '';
  casoContactoId = '';
  casoTitulo = '';
  casoOrigen = 'whatsapp';
  casoValor = 0;

  // Formulario Nuevo Paciente
  pacTipoDoc = 'CC';
  pacNumeroDoc = '';
  pacNombres = '';
  pacApellidos = '';
  pacTelefono = '';
  pacCorreo = '';
  pacOrigen = 'whatsapp';
  pacConsentimiento = true;

  constructor() {
    this.urlActual.set(this.router.url || '/pendientes?vista=inicio');
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.urlActual.set(e.urlAfterRedirects || e.url);
      });

    effect(() => {
      const m = this.crm.modalNuevoCaso();
      if (m.abierto) {
        this.errorModal.set(null);
        this.crm.obtenerEmbudos().subscribe(lista => {
          this.embudos.set(lista);
          this.casoEmbudoId = m.embudoId || lista[0]?.id || '';
          const emb = lista.find(e => e.id === this.casoEmbudoId);
          this.casoEtapaId = m.etapaId || emb?.etapas[0]?.id || '';
        });
        this.crm.buscarContactos().subscribe(c => {
          this.contactos.set(c);
          this.casoContactoId = m.contactoId || c[0]?.id || '';
        });
      }
    });
  }

  irAModulo(mod: ModuloMenuNexa) {
    // Agregar a la barra inferior de pestañas abiertas si no existe
    this.pestanasAbiertas.update(actuales => {
      if (actuales.some(x => x.id === mod.id)) return actuales;
      return [...actuales.slice(-2), mod];
    });

    if (mod.vista) {
      this.router.navigate([mod.ruta], { queryParams: { vista: mod.vista } });
    } else {
      this.router.navigate([mod.ruta]);
    }
  }

  cerrarPestanaDock(mod: ModuloMenuNexa, ev: Event) {
    ev.stopPropagation();
    const restantes = this.pestanasAbiertas().filter(x => x.id !== mod.id);
    if (restantes.length === 0) {
      this.pestanasAbiertas.set([this.modulosMenu[0]]);
      this.irAModulo(this.modulosMenu[0]);
      return;
    }
    this.pestanasAbiertas.set(restantes);
    if (this.esModuloActivo(mod)) {
      this.irAModulo(restantes[restantes.length - 1]);
    }
  }

  esModuloActivo(mod: ModuloMenuNexa): boolean {
    const url = this.urlActual();
    const [pathPart, queryPart] = url.split('?');
    const params = new URLSearchParams(queryPart || '');
    const vistaParam = params.get('vista');

    if (mod.ruta === '/pacientes') return pathPart.startsWith('/pacientes');
    if (mod.ruta === '/conversaciones') return pathPart.startsWith('/conversaciones');
    if (mod.ruta === '/procesos') {
      if (!pathPart.startsWith('/procesos')) return false;
      if (mod.vista === 'quirurgicos') return vistaParam === 'quirurgicos';
      return vistaParam !== 'quirurgicos';
    }
    if (mod.ruta === '/pendientes') {
      if (!pathPart.startsWith('/pendientes') && pathPart !== '/') return false;
      const vistaEfectiva = vistaParam || 'inicio';
      return mod.vista === vistaEfectiva;
    }
    return false;
  }

  etiquetaCategoriaActual(): string {
    switch (this.categoriaModulo()) {
      case 'clinica': return 'Clínica y Cirugía';
      case 'atencion': return 'Atención y Pacientes';
      case 'omnicanal': return 'Omnicanal y Tiempos';
      default: return 'Todos los módulos';
    }
  }

  seleccionarCategoriaMenu(cat: 'todos' | 'clinica' | 'atencion' | 'omnicanal') {
    this.categoriaModulo.set(cat);
    this.menuCategoriasAbierto.set(false);
  }

  ejecutarBusquedaGlobal() {
    const q = this.busquedaGlobal.trim();
    if (!q) return;
    this.router.navigate(['/pacientes'], { queryParams: { q } });
  }

  etapasEmbudoModal() {
    return this.embudos().find(e => e.id === this.casoEmbudoId)?.etapas ?? [];
  }

  alCambiarEmbudoModal(id: string) {
    this.casoEmbudoId = id;
    const emb = this.embudos().find(e => e.id === id);
    this.casoEtapaId = emb?.etapas[0]?.id || '';
  }

  autocompletarDesdeClinica() {
    const num = this.pacNumeroDoc.trim();
    if (!num) {
      this.avisoClinico.set('Escribe primero el número de documento (ej. 45123987 o 73456120).');
      return;
    }
    this.consultandoClinica.set(true);
    this.avisoClinico.set(null);
    this.crm.consultarPacienteClinico(this.pacTipoDoc, num).subscribe({
      next: (pac) => {
        this.consultandoClinica.set(false);
        this.pacNombres = pac.nombres;
        this.pacApellidos = pac.apellidos ?? '';
        this.pacTelefono = pac.telefono ?? '';
        this.pacCorreo = pac.correo ?? '';
        this.avisoClinico.set(`Paciente encontrado en sistema clínico (${pac.aseguradora ?? 'Particular'}).`);
      },
      error: (err) => {
        this.consultandoClinica.set(false);
        this.avisoClinico.set(err.error?.error ?? 'No se encontró en la fuente clínica; puedes digitar los datos.');
      }
    });
  }

  cerrarModalCaso() {
    this.crm.modalNuevoCaso.set({ abierto: false });
    this.casoTitulo = '';
    this.casoValor = 0;
    this.errorModal.set(null);
  }

  guardarNuevoCaso() {
    if (!this.casoTitulo.trim() || !this.casoEmbudoId || !this.casoContactoId) {
      this.errorModal.set('Completa el paciente, el proceso y la solicitud del caso.');
      return;
    }
    this.crm.crearCaso({
      embudoId: this.casoEmbudoId,
      etapaId: this.casoEtapaId || null,
      contactoId: this.casoContactoId,
      titulo: this.casoTitulo.trim(),
      origen: this.casoOrigen,
      valor: Number(this.casoValor) || 0,
      fechaVencimiento: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
    }).subscribe({
      next: () => {
        this.cerrarModalCaso();
        this.crm.notificarCambio();
      },
      error: (err) => this.errorModal.set(err.error?.error ?? 'No se pudo registrar el caso.')
    });
  }

  cerrarModalPaciente() {
    this.crm.modalNuevoPaciente.set(false);
    this.pacNombres = '';
    this.pacApellidos = '';
    this.pacNumeroDoc = '';
    this.pacTelefono = '';
    this.pacCorreo = '';
    this.errorModal.set(null);
    this.avisoClinico.set(null);
  }

  guardarNuevoPaciente() {
    if (!this.pacNombres.trim()) {
      this.errorModal.set('El nombre del paciente es obligatorio.');
      return;
    }
    const numDoc = this.pacNumeroDoc.trim();
    this.crm.crearContacto({
      nombres: this.pacNombres.trim(),
      apellidos: this.pacApellidos.trim() || null,
      tipoDocumento: numDoc ? this.pacTipoDoc : null,
      numeroDocumento: numDoc || null,
      telefono: this.pacTelefono.trim() || null,
      correo: this.pacCorreo.trim() || null,
      origen: this.pacOrigen,
      consentimientoDatos: this.pacConsentimiento,
    }).subscribe({
      next: (r) => {
        this.cerrarModalPaciente();
        this.crm.notificarCambio();
        this.router.navigate(['/pacientes', r.id]);
      },
      error: (err) => this.errorModal.set(err.error?.error ?? 'No se pudo registrar el paciente.')
    });
  }

  inicialesUsuario(): string {
    const n = this.sesion.usuario()?.nombre ?? 'NM';
    return n.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
  }
}
